// Whole-window transcript export — dumps every open chat in the
// current ChatConsole as a single JSON blob, so a stress-testing
// loop ("run a query, look at results, tweak prompt, repeat") can
// pull the actual transcripts out for analysis with another tool
// (claude, jq, a python notebook) instead of eyeballing the UI.
//
// Two entry points:
//   • `bundleChats(chats)` — pure serializable shape, for callers
//     that want to ship the JSON somewhere themselves.
//   • `downloadChatsBundle(chats, opts?)` — triggers a browser
//     download of the bundle. The `__exportChats()` window global
//     wired by ChatConsole calls this against the live chats ref so
//     a DevTools-console invocation gets the current state.

// Strip values React / pi-agent-core wedge into chat items that
// don't JSON-serialize cleanly (closures, abort controllers, DOM
// nodes, circular refs). The replacer is best-effort — anything
// genuinely weird becomes `null` rather than throwing.
//
// Exported so chatSerializer.js can reuse the exact same stripping
// rules when serializing for IndexedDB persistence — single source
// of truth for what counts as "JSON-safe."
export function makeReplacer() {
    // Ancestor stack for TRUE cycle detection. A grow-only `seen` set
    // (what this used to be) can't tell a real cycle — a back-edge to
    // an ancestor — from a shared-but-acyclic reference: the same
    // object reached from two sibling positions (a DAG). JSON.stringify
    // walks depth-first and never signals subtree *exit*, so a set that
    // only grows flags every shared ref as "[circular]". Chat snapshots
    // are exactly that case — `turn.snapshot` (pi-agent messages) shares
    // object identity with the `items` tree — so the old replacer turned
    // every snapshot into unusable "[circular]" junk both in exports and
    // in IndexedDB persistence, which also silently broke fork/rewind.
    //
    // Instead, track only the current ancestor chain. The replacer's
    // `this` is the holder (immediate parent) of `value`; the holder is
    // itself already on the stack, so we pop back to it and whatever
    // remains is exactly value's ancestors. A hit there is a genuine
    // cycle; a shared sibling ref is serialized in full (duplicated,
    // which is the correct JSON representation — JSON has no references).
    const stack = [];
    return function replacer(_key, value) {
        if (typeof value === "function") return undefined;
        if (value instanceof Error) {
            return { __error: true, name: value.name, message: value.message, stack: value.stack };
        }
        if (typeof value === "object" && value !== null) {
            // DOM / AbortController / similar host objects: keep
            // out of the dump.
            if (value instanceof Element) return undefined;
            if (typeof AbortController !== "undefined" && value instanceof AbortController) return undefined;
            if (typeof AbortSignal !== "undefined" && value instanceof AbortSignal) return undefined;
            // Unwind to the current holder, then check ancestors only.
            while (stack.length > 0 && stack[stack.length - 1] !== this) {
                stack.pop();
            }
            if (stack.includes(value)) return "[circular]";
            stack.push(value);
        }
        return value;
    };
}

// Turn the raw chats array into a JSON-safe bundle. Tries to keep
// everything useful for replay/inspection — items (with details),
// per-chat config (thinkingLevel, scope mode, kernel suggestions),
// and the live pi-agent messages exposed via spawn_subagent state.
export function bundleChats(chats, { exportedAt = Date.now() } = {}) {
    const replacer = makeReplacer();
    const safeChats = JSON.parse(JSON.stringify(chats ?? [], replacer));
    return {
        kind: "origin.chats-bundle",
        version: 1,
        exportedAt,
        url: typeof window !== "undefined" ? window.location?.href : null,
        chatCount: Array.isArray(safeChats) ? safeChats.length : 0,
        chats: safeChats,
    };
}

// Trigger a `<tenant>-<timestamp>.json` download of the bundle.
// Returns the bundle for chaining / debugging.
export function downloadChatsBundle(chats, { filename, tenantId } = {}) {
    const bundle = bundleChats(chats);
    const stamp = new Date(bundle.exportedAt).toISOString().replace(/[:.]/g, "-");
    const namePrefix = tenantId ? `${tenantId}-` : "";
    const fname = filename || `origin-chats-${namePrefix}${stamp}.json`;
    const blob = new Blob([JSON.stringify(bundle, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = fname;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 1000);
    return bundle;
}
